/**
 * The per-device crop box (`devices.bounding_area`, POS2-957) as the screens
 * showing a capture need it.
 *
 * Classification crops the frame to this box before the model sees it, so a screen
 * that renders the whole frame shows a reviewer more of the kitchen than the
 * classifier ever looked at (POS2-1105). The box is absolute pixels in the
 * original's own space — which is what the browser is served, presigned and
 * unresized — so it lines up with the `<img>`'s natural size.
 */

/** `[x, y, w, h]`, exactly as the column stores it. The DB CHECK guarantees the
 * four elements, x/y >= 0 and w/h > 0. */
export type BoundingArea = [number, number, number, number];

export type Natural = { width: number; height: number };

export type Crop = { x: number; y: number; w: number; h: number };

/**
 * The crop to render, or null to render the original untouched.
 *
 * Null in three cases: the device has no box (it crops on-device), the image has
 * not reported its natural size yet, and the box overruns the frame. The last one
 * is the classifier's own `crop_out_of_bounds` fallback — real old-system boxes do
 * overrun, and the two must agree or the screen claims a crop that never happened.
 */
export function resolveCrop(box: BoundingArea | null, natural: Natural | null): Crop | null {
  if (!box || !natural) return null;
  const [x, y, w, h] = box;
  if (x + w > natural.width || y + h > natural.height) return null;
  return { x, y, w, h };
}

/**
 * `transform-origin` for a zoom of the rendered crop, as a percentage pair.
 *
 * `fx`/`fy` are where the cursor sits within the visible frame, 0–1. The element
 * being scaled is the whole image, so a crop's offset has to be mapped back in or
 * the zoom lands somewhere else entirely. With no crop the box is the whole image
 * and this reduces to the cursor position.
 */
export function zoomOrigin(
  fx: number,
  fy: number,
  crop: Crop | null,
  natural: Natural | null,
): string {
  if (!crop || !natural) return `${fx * 100}% ${fy * 100}%`;
  const x = ((crop.x + fx * crop.w) / natural.width) * 100;
  const y = ((crop.y + fy * crop.h) / natural.height) * 100;
  return `${x}% ${y}%`;
}
